import { Button, CalendarOption, IconButton, SingleSelectControl } from "@dgs/core";
import { addMonths, setMonth, setYear, subMonths } from "date-fns";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useCalendarProvider } from "./CalendarProvider";

const Wrapper = styled.div`
	display: grid;
	grid-auto-flow: column;
	gap: ${({ theme }) => theme.spacing(2)};
	align-items: center;
	justify-content: start;
	grid-template-rows: min-content min-content;
	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-rows: min-content;
	}
`;
const Group = styled.div`
	gap: ${({ theme }) => theme.spacing(2)};
	display: flex;
`;
const StyledSelectControl: typeof SingleSelectControl = styled(SingleSelectControl)`
	padding-bottom: 0;
	margin: 0;
	width: 8em;
`;

const getLabel = (o: CalendarOption) => o.name;
const getValue = (o: CalendarOption) => o.value;

interface CalendarNavigationProps {
	disablePrevious?: boolean;
	disableNext?: boolean;
	monthOptions: CalendarOption[];
	yearOptions: CalendarOption[];
	showTodayButton?: boolean;
}
export const CalendarNavigation: FC<CalendarNavigationProps> = (props) => {
	const { selectedDate, setSelectedDate } = useCalendarProvider();
	const { monthOptions, yearOptions, disableNext = false, disablePrevious = false } = props;
	const { t } = useTranslation();
	const isFirstMonth = useMemo(
		() =>
			selectedDate.getMonth() === monthOptions.at(0)?.value && selectedDate.getFullYear() === yearOptions.at(0)?.value,
		[monthOptions, selectedDate, yearOptions],
	);
	const isLastMonth = useMemo(
		() =>
			selectedDate.getMonth() === monthOptions.at(-1)?.value &&
			selectedDate.getFullYear() === yearOptions.at(-1)?.value,
		[monthOptions, selectedDate, yearOptions],
	);
	const selectYearOptions = useMemo(
		() =>
			yearOptions.length
				? yearOptions
				: [{ name: selectedDate.getFullYear().toString(), value: selectedDate.getFullYear() }],
		[selectedDate, yearOptions],
	);

	return (
		<Wrapper>
			<Group>
				<Button type="button" onClick={() => setSelectedDate(new Date())} size="small">
					{t("Today")}
				</Button>
				<IconButton
					type="button"
					size="small"
					title={t("Previous month")}
					data-testid="prev-month"
					disabled={disablePrevious || isFirstMonth}
					onClick={() => setSelectedDate(subMonths(selectedDate, 1))}
					icon="caret_left"
				/>
				<IconButton
					type="button"
					size="small"
					title={t("Next month")}
					data-testid="next-month"
					disabled={disableNext || isLastMonth}
					onClick={() => setSelectedDate(addMonths(selectedDate, 1))}
					icon="caret"
				/>
			</Group>
			<Group>
				<StyledSelectControl<number, any>
					getValue={getValue}
					getLabel={getLabel}
					clearable={false}
					options={monthOptions}
					value={selectedDate.getMonth()}
					onChange={(e) => e.target.value !== null && setSelectedDate(setMonth(selectedDate, e.target.value))}
					name="months"
				/>
				<StyledSelectControl
					getValue={getValue}
					getLabel={getLabel}
					clearable={false}
					options={selectYearOptions}
					value={selectedDate.getFullYear()}
					onChange={(e) => e.target.value !== null && setSelectedDate(setYear(selectedDate, e.target.value))}
					name="years"
				/>
			</Group>
		</Wrapper>
	);
};
